<template>
  <div class="studioSelection">
    <v-dialog v-model="cantonDialogVisible" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card class="dialog-card-fullscreen">
        <v-card-title class="pa-0">
          <div style="height: 80px" class="dialog-header-fullscreen">
            <div class="dialog-header-title-bar">
              <v-text-field
                v-model="searchQuery"
                solo
                flat
                hide-details
                label="Studio, Ort oder Adresse suchen..."
                prepend-inner-icon="mdi-magnify"
                clearable></v-text-field>
              <v-btn icon @click="cantonDialogVisible = false"><v-icon>mdi-close</v-icon></v-btn>
            </div>
          </div>
        </v-card-title>

        <v-card-text class="pa-0 d-flex flex-grow-1">
          <div v-if="$vuetify.breakpoint.mdAndUp" class="canton-nav">
            <v-list dense>
              <v-list-item-group color="primary">
                <v-list-item v-for="group in filteredDirectory" :key="group.canton" @click="scrollToCanton(group.canton)">
                  <v-list-item-content>
                    <v-list-item-title>{{ group.canton }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </div>

          <div class="studio-content-area" ref="studioContentArea">
            <div v-if="filteredDirectory.length > 0">
              <section v-for="group in filteredDirectory" :key="group.canton" :ref="`canton-${group.canton}`" class="canton-section">
                <h2 class="headline font-weight-bold mb-4 ml-1">{{ group.canton }}</h2>
                <v-row>
                  <v-col v-for="studio in group.studios" :key="studio._id" cols="6" sm="4" md="4" lg="3">
                    <v-card
                      hover
                      outlined
                      class="studio-grid-card"
                      :class="{ 'selected-studio-card': studio._id === getSelectedStudioId }"
                      @click="selectStudioFromDialog(studio)">
                      <v-card-text class="pa-2 pa-sm-3">
                        <div class="font-weight-bold text-truncate">{{ studio.name }}</div>
                        <div class="caption text-truncate">{{ studio.adresse.split(",")[0] }}</div>
                        <div class="caption text-truncate">{{ studio.adresse.split(",")[1] }}</div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </section>
            </div>
            <div v-else class="text-center pa-16 grey--text">
              <v-icon large class="d-block mx-auto mb-3">mdi-magnify-close</v-icon>
              Keine Studios für deine Suche gefunden.
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogVisible" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card flat color="transparent">
        <v-btn v-if="false" @click="closeDialog" class="close-btn" fab dark small color="rgba(0, 0, 0, 0.5)"><v-icon>mdi-close</v-icon></v-btn>

        <l-map v-if="!loading" ref="map" :zoom="zoom" :center="center" class="map-background" :key="mapKey" :options="{ zoomControl: false }">
          <l-tile-layer :url="attributionUrl" :attribution="attribution" />

          <l-marker
            v-for="m in markers"
            :key="m.id"
            :icon="getMarkerIcon(m.id)"
            :lat-lng="[m.position.lat, m.position.lng]"
            @click="scrollToStudio(m.id)" />
        </l-map>

        <div class="content-overlay">
          <div v-if="!loading" class="header-bar">
            <h1 class="header-title mb-0">STANDORT</h1>

            <v-btn @click="cantonDialogVisible = true" class="canton-selector-btn" text>
              {{ selectedCanton || "Alle Kantone" }}

              <v-icon right>mdi-menu-down</v-icon>
            </v-btn>
          </div>

          <div v-if="!loading" class="studio-carousel" ref="carousel">
            <div
              v-for="(studio, index) in filteredStudios"
              :key="studio._id"
              class="studio-slide"
              :data-studio-id="studio._id"
              :ref="`slide-${studio._id}`">
              <v-card class="studio-card-tiktok" :light="!$vuetify.theme.dark" :dark="$vuetify.theme.dark">
                <v-card-title style="font-weight: 1000; font-style: italic" class="card-title justify-center text-center display-1 pt-6 mb-0">{{
                  studio.name.toUpperCase()
                }}</v-card-title>

                <v-card-subtitle class="overline mt-n2">{{ studio.canton }}</v-card-subtitle>

                <v-card-text class="card-content">
                  <div class="studio-info">
                    <p v-if="studio.code !== 'ot'"><v-icon class="pr-2">mdi-map-marker</v-icon> {{ studio.adresse }}</p>

                    <p v-if="studio.email && studio.code !== 'ot'">
                      <v-icon class="pr-2">mdi-email</v-icon><a :href="'mailto:' + studio.email">{{ studio.email }}</a>
                    </p>

                    <p v-if="studio.telefon && studio.code !== 'ot'">
                      <v-icon class="pr-2">mdi-phone</v-icon><a :href="'tel:' + studio.telefon">{{ studio.telefon }}</a>
                    </p>
                  </div>

                  <attendance-trainer-list
                    :showBades="userService.hasRight('tp_create')"
                    v-if="studio"
                    :maxWidth="'620px'"
                    :marginXAuto="true"
                    :noGutters="true"
                    :showTrainerButtons="false"
                    :key="studio._id"
                    :studio="studio._id"></attendance-trainer-list>

                  <div class="opening-hours mt-4" v-if="studio.openingHours || studio.alwaysCheckin">
                    <v-chip-group v-model="studio.selectedHours" mandatory active-class="primaryChip" center-active>
                      <v-chip v-if="studio.alwaysCheckin" value="Checkin" small>Check-in</v-chip>

                      <v-chip v-for="hours in studio.openingHours" :key="hours.title" :value="hours.title" small>{{ hours.title }}</v-chip>
                    </v-chip-group>

                    <div class="hours-container mt-2">
                      <template v-if="getOpeningHoursData(studio)">
                        <div v-for="(item, i) in getOpeningHoursData(studio).hours" :key="i" class="hours-row" style="align-items: center">
                          <span>{{ item.text }}</span>

                          <div class="text-right">
                            <span v-if="item.from && item.to">{{ item.from + " - " + item.to }}</span>
                            <span v-if="item.from2 && item.to2">
                              <br v-if="item.from && item.to" />
                              {{ item.from2 + " - " + item.to2 }}
                            </span>
                          </div>
                        </div>
                      </template>

                      <div v-if="studio.selectedHours === 'Checkin' && studio.alwaysCheckin" class="hours-row">
                        <span>MO - SO / FT</span>

                        <span class="text-right">24H</span>
                      </div>
                    </div>
                  </div>
                </v-card-text>

                <v-card-actions class="card-actions">
                  <v-btn color="primary" x-large rounded block @click="pickStudio(studio)"
                    ><v-icon color="white" left>mdi-check-circle-outline</v-icon>Studio auswählen</v-btn
                  >
                </v-card-actions>
              </v-card>

              <div class="scroll-indicator" v-if="index < filteredStudios.length - 1">
                <v-icon large color="white">mdi-chevron-down</v-icon>
              </div>
            </div>
          </div>

          <v-row style="background-color: white" v-if="loading" align="center" justify="center" class="fill-height"
            ><v-progress-circular indeterminate size="64" color="primary"></v-progress-circular
          ></v-row>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "../../api"
import socketService from "../../socketService"
import { mapGetters, mapActions, mapState } from "vuex"

import L from "leaflet"

import { LMap, LTileLayer, LMarker } from "vue2-leaflet"

import userService from "../../services/userService"

import attendanceTrainerList from "@/views/dashboard/attendance/attendanceTrainerList.vue"
import { store } from "@/store/stores"
import "leaflet/dist/leaflet.css"

delete L.Icon.Default.prototype._getIconUrl

L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),

  iconUrl: require("leaflet/dist/images/marker-icon.png"),

  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
})

export default {
  name: "StudioPickerModern",

  components: { LMap, LTileLayer, LMarker, attendanceTrainerList },

  props: {
    visible: { type: Boolean, default: false },
  },

  data: () => ({
    userService,
    mapKey: 0,
    loading: false,
    activeCantonNav: 0,
    dialogVisible: false,

    wasNavVisible: false,

    studios: [],

    selectedCanton: null,

    activeStudioId: null,
    targetStudioId: null, // <-- DIESE ZEILE HINZUFÜGEN

    observer: null,

    zoom: 8,

    center: [47.2, 8.5],

    attributionUrl: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",

    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',

    cantonDialogVisible: false,

    searchQuery: "",
  }),

  computed: {
    ...mapState("navigationStoreNotPersisted", ["navigationVisible"]),
    ...mapState("navigationStore", ["reservationTypes"]),
    ...mapState("studioStore", ["selectedStudio"]),
    ...mapGetters("studioStore", ["getStudios", "getSelectedStudioId"]),
    markers() {
      return this.studios

        .filter((s) => s.location?.lat && s.location?.long)

        .map((s) => ({
          id: s._id,
          position: { lat: s.location.lat, lng: s.location.long },
        }))
    },

    /*  filteredStudios() {
      if (!this.selectedCanton) return this.studios.filter((s) => s.code !== "ot")

      return this.studios.filter((s) => s.canton === this.selectedCanton && s.code !== "ot")
    },*/
    filteredStudios() {
      // Gibt immer alle Studios zurück, damit man durch alle scrollen kann.
      return this.studios.filter((s) => s.code !== "ot")
    },

    activeStudio() {
      return this.studios.find((s) => s._id === this.activeStudioId) || null
    },

    filteredDirectory() {
      const query = this.searchQuery ? this.searchQuery.toLowerCase() : ""

      let filtered = this.studios

      if (query) {
        filtered = this.studios.filter(
          (studio) =>
            studio.name.toLowerCase().includes(query) ||
            studio.adresse.toLowerCase().includes(query) ||
            (studio.canton && studio.canton.toLowerCase().includes(query))
        )
      }

      const grouped = filtered.reduce((acc, studio) => {
        const canton = studio.canton || "Unbekannt"

        if (!acc[canton]) {
          acc[canton] = []
        }

        acc[canton].push(studio)

        return acc
      }, {})

      return Object.entries(grouped)
        .map(([canton, studios]) => ({ canton, studios }))
        .sort((a, b) => {
          const alibeyCanton = "Alibey"
          if (a.canton === alibeyCanton && b.canton !== alibeyCanton) return 1
          if (b.canton === alibeyCanton && a.canton !== alibeyCanton) return -1
          return a.canton.localeCompare(b.canton)
        })
    },
  },

  methods: {
    ...mapActions("navigationStoreNotPersisted", ["toggleNavigationBarVisibility"]),
    ...mapActions("studioStore", ["setSelectedStudio", "setReservationTypes"]),
    ...mapActions("deviceStore", ["resetDeviceList"]),

    scrollToCanton(cantonId, behavior = "smooth") {
      this.$nextTick(() => {
        const contentArea = this.$refs.studioContentArea
        const element = this.$refs[`canton-${cantonId}`]
        if (contentArea && element && element[0]) {
          const topPos = element[0].offsetTop - contentArea.offsetTop
          contentArea.scrollTo({
            top: topPos, // Etwas Platz oben lassen
            behavior: behavior, // 'smooth' oder 'auto'
          })
        }
      })
    },

    async init() {
      await store.restored
      this.loading = true

      try {
        let studiosData = null
        if (this.getStudios && this.getStudios.length > 0) {
          studiosData = JSON.parse(JSON.stringify(this.getStudios))
        } else {
          studiosData = await api.fetchStudios()
        }

        this.studios = studiosData.map((studio) => ({
          ...studio,

          selectedHours: studio.alwaysCheckin ? "Checkin" : studio.openingHours?.[0]?.title ?? null,
        }))

        this.$nextTick(() => {
          this.setupObserver()

          if (this.selectedStudio) {
            this.targetStudioId = this.selectedStudio._id
            this.selectedCanton = this.selectedStudio.canton
          } else {
            this.cantonDialogVisible = true
            // Wenn kein Studio ausgewählt ist, das erste Studio aktivieren
            this.targetStudioId = this.filteredStudios[0]?._id ?? null
            this.selectedCanton = this.filteredStudios[0]?.canton ?? null
          }

          this.mapKey += 1 // Force re-render of the map
        })
      } catch (e) {
        console.error("Failed to fetch studios:", e)
      }

      this.loading = false
    },

    // in methods:
    /* selectStudioFromDialog(studio) {
      this.cantonDialogVisible = false
      this.searchQuery = ""

      // Prüfen, ob der Kanton derselbe bleibt.
      if (this.selectedCanton === studio.canton) {
        // FALL 1: Kanton ist gleich, der Watcher wird NICHT ausgelöst.
        // Wir lösen das Update manuell aus.
        this.activeStudioId = studio._id
        this.scrollToStudio(studio._id)
        // Der Watcher auf `activeStudioId` kümmert sich um das Update der Karte.
      } else {
        // FALL 2: Kanton wechselt. Die bisherige Logik mit dem Watcher wird verwendet.
        this.targetStudioId = studio._id
        this.selectedCanton = studio.canton
      }
    },*/
    selectStudioFromDialog(studio) {
      this.cantonDialogVisible = false
      this.searchQuery = ""

      // Setze den Kanton direkt, damit die Anzeige im Header stimmt,
      // bevor der Scroll-Effekt abgeschlossen ist.
      this.selectedCanton = studio.canton

      // Scrolle einfach zum ausgewählten Studio. Der Observer aktualisiert dann
      // die activeStudioId, und der Watcher kümmert sich um den Rest (Map-Update etc.).
      this.scrollToStudio(studio._id)
    },
    getOpeningHoursData(studio) {
      if (!studio || !studio.selectedHours || (studio.selectedHours === "Checkin" && studio.alwaysCheckin)) return null

      return studio.openingHours.find((oh) => oh.title === studio.selectedHours)
    },

    closeDialog() {
      this.dialogVisible = false
    },

    setupObserver() {
      if (this.observer) this.observer.disconnect()

      const options = {
        root: this.$refs.carousel,
        rootMargin: "0px",
        threshold: 0.7,
      }

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.activeStudioId = entry.target.dataset.studioId
          }
        })
      }, options)

      this.filteredStudios.forEach((studio) => {
        const el = this.$refs[`slide-${studio._id}`]

        if (el?.[0]) this.observer.observe(el[0])
      })
    },

    getMarkerIcon(studioId) {
      const isActive = this.activeStudioId === studioId

      const iconHtml = `<div class="marker-pin ${isActive ? "active" : ""}"></div>`

      return L.divIcon({
        className: "custom-div-icon",
        html: iconHtml,
        iconSize: [30, 42],
        iconAnchor: [15, 42],
      })
    },

    updateMapForActiveStudio(animate = true) {
      if (!this.activeStudio?.location) return

      const { lat, long: lng } = this.activeStudio.location

      const targetLatLng = [lat - 0.008, lng] // Remove manual offset if panBy is used

      this.$nextTick(() => {
        if (this.$refs.map) {
          const map = this.$refs.map.mapObject

          const zoomLevel = 15

          const flyAndPan = () => {
            map.flyTo(targetLatLng, zoomLevel, {
              animate: true,
              duration: 1.0,
            })
          }

          if (animate) flyAndPan()
          else {
            map.setView(targetLatLng, zoomLevel)

            /*  const yOffset = map.getSize().y * 0.2

            map.panBy([0, -yOffset], { animate: false })*/
          }
        }
      })
    },

    scrollToStudio(studioId, smooth = true) {
      const studioElement = this.$refs[`slide-${studioId}`]

      if (studioElement?.[0]) {
        studioElement[0].scrollIntoView({
          // Wir verwenden den neuen 'smooth'-Parameter, um das Verhalten zu steuern.
          // 'auto' bewirkt einen sofortigen Sprung ohne Animation.
          behavior: smooth ? "smooth" : "auto",
          block: "start",
        })
      }
    },

    async pickStudio(studio) {
      this.loading = true

      try {
        let user = JSON.parse(localStorage.getItem("user"))

        if (user?.user) {
          user.user.studio_id = studio.code
          user.user.studio = studio
          localStorage.setItem("user", JSON.stringify(user))
        }

        await api.pickStudio({ studio_code: studio.code })

        this.setSelectedStudio(studio)

        if (socketService.socket && socketService.socket.connected) {
          socketService.socket.emit("change_studio", { newStudioId: studio.code })
        }

        if (!this.reservationTypes) {
          try {
            const [reservationAvailability] = await Promise.all([api.reservationTool.fetchReservationAvailability()])

            if (reservationAvailability && reservationAvailability.length > 0) {
              this.setReservationTypes(reservationAvailability)
            }
          } catch (e) {}
        }

        this.resetDeviceList()

        this.$emit("studio-picked")

        this.closeDialog()
      } catch (e) {
        console.error("Error picking studio:", e)
        this.loading = false
      }
    },
  },

  watch: {
    cantonDialogVisible(isVisible) {
      if (isVisible) {
        // Wenn der Dialog sichtbar wird
        this.$nextTick(() => {
          if (this.getSelectedStudioId) {
            const currentStudio = this.studios.find((s) => s._id === this.getSelectedStudioId)
            if (currentStudio) {
              // Scrolle sofort (auto) zur Sektion des ausgewählten Studios
              this.scrollToCanton(currentStudio.canton, "auto")
            }
          }
        })
      }
    },
    visible: {
      handler(newVal) {
        this.dialogVisible = newVal

        if (newVal) {
          this.wasNavVisible = this.navigationVisible

          if (this.wasNavVisible) this.toggleNavigationBarVisibility()

          this.init()
        } else {
          if (this.wasNavVisible) {
            this.toggleNavigationBarVisibility()

            this.wasNavVisible = false
          }
          console.log("setting false")
          let ref = this
          setTimeout((e) => {
            ref.loading = false
          }, 300)
        }
      },

      immediate: true,
    },

    dialogVisible(newVal) {
      if (!newVal) this.$emit("update:visible", false)
    },

    filteredStudios() {
      this.$nextTick(() => {
        if (this.$refs.carousel) this.$refs.carousel.scrollTop = 0

        if (this.targetStudioId && this.filteredStudios.some((s) => s._id === this.targetStudioId)) {
          this.activeStudioId = this.targetStudioId
          this.scrollToStudio(this.targetStudioId, false)

          // HIER DIE LÖSUNG: Rufe die Kartenaktualisierung direkt und ohne Animation auf.
          this.updateMapForActiveStudio(false)

          this.targetStudioId = null
        } else {
          this.activeStudioId = this.filteredStudios[0]?._id ?? null
          // Auch hier zur Sicherheit die Karte initial positionieren.
          this.updateMapForActiveStudio(false)
        }

        this.setupObserver()
      })
    },
    activeStudioId(newId, oldId) {
      // Die Methode wird nun NUR ausgeführt, wenn oldId NICHT null ist.
      // Das bedeutet, sie reagiert nur auf Aktionen des Benutzers nach der Initialisierung.
      if (newId && newId !== oldId && oldId !== null) {
        const newActiveStudio = this.studios.find((s) => s._id === newId)
        if (newActiveStudio) {
          this.selectedCanton = newActiveStudio.canton
        }
        // Hier bleibt die Animation (Standard 'true') erhalten für ein flüssiges Erlebnis.
        this.updateMapForActiveStudio()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap");

.dialog-card-fullscreen {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #f4f6f8;
}

.dialog-header-fullscreen {
  width: 100%;
  padding: 16px 24px;
  border-bottom: 1px solid #e0e0e0;
  background-color: white;
  z-index: 10;
}

.canton-nav {
  width: 240px;
  flex-shrink: 0;
  height: calc(100vh - 80px); // Höhe an Header angepasst
  overflow-y: auto;
  border-right: 1px solid #e0e0e0;
  background-color: white;
}

.studio-content-area {
  flex-grow: 1;
  overflow-y: auto;
  padding: 24px;
  height: calc(100vh - 80px); // Höhe an Header angepasst
}

.canton-section {
  margin-bottom: 40px;
}

.studio-grid-card {
  /* Grundlegende Transparenz und Weichzeichner für den Glas-Effekt */
  background-color: rgba(255, 255, 255, 0.45); /* Weiss mit 45% Deckkraft */
  backdrop-filter: blur(15px) saturate(150%); /* Der eigentliche Weichzeichner-Effekt */

  /* Feiner Rand, der das Licht auf der Glaskante simuliert */
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px !important; /* Etwas grösserer Radius für weichere Ecken */

  /* Schatten für die Tiefenwirkung, damit die Karte schwebt */
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.1);

  /* Beibehaltung der bestehenden Layout- und Hover-Effekte */
  transition: all 0.2s ease-in-out;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;

  &:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: 0 12px 32px 0 rgba(31, 38, 135, 0.15);
    background-color: rgba(255, 255, 255, 0.6); /* Etwas heller beim Hovern */
  }

  /* Textfarbe explizit setzen für besseren Kontrast auf dem Glas */
  .v-card__text {
    color: #333;
  }
}

.selected-studio-card {
  /* Primärfarbe mit Transparenz für den Hintergrund */
  background-color: rgba(0, 153, 63, 0.8); /* --v-primary-base mit Transparenz */
  backdrop-filter: blur(15px) saturate(180%);

  /* Hellerer, farbiger Rand zur Hervorhebung */
  border: 1.5px solid rgba(0, 153, 63, 0.8);

  /* Textfarbe auf Weiss ändern für Lesbarkeit */
  .v-card__text {
    color: white !important;
    font-weight: 500;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2); /* Leichter Textschatten */
  }
}

// MOBILE ANPASSUNGEN
@media (max-width: 959px) {
  .studio-content-area {
    padding: 12px;
  }
  .v-dialog__content .headline {
    font-size: 1.25rem !important; // Kleinere Überschriften auf Mobile
  }
}

// Responsive Anpassungen für Mobilgeräte
@media (max-width: 768px) {
  .v-card-text.d-flex {
    flex-direction: column;
    align-items: stretch !important;
  }

  .canton-nav {
    display: none; // Navigationsleiste auf kleinen Bildschirmen ausblenden
  }

  .studio-content-area {
    width: 100%;
    padding: 16px;
    height: calc(100vh - 80px);
  }

  .canton-section {
    margin-bottom: 32px;
  }
}

.primaryChip {
  background-color: var(--v-primary) !important;

  color: white !important;
}

.close-btn {
  position: fixed;

  top: 20px;

  right: 20px;

  z-index: 1002;
}

.map-background {
  position: fixed;

  top: 0;

  left: 0;

  z-index: 1;

  width: 100vw;

  height: 100vh;
}

.content-overlay {
  position: absolute;

  top: 0;

  left: 0;

  width: 100%;

  height: 100%;

  z-index: 2;

  pointer-events: none;

  display: flex;

  flex-direction: column;
}

.header-bar {
  pointer-events: auto;

  position: absolute;

  top: 20px;

  left: 50%;

  transform: translateX(-50%);

  width: 90%;

  max-width: 400px;

  z-index: 3;

  padding: 8px 8px 8px 20px;

  display: flex;

  align-items: center;

  justify-content: space-between;

  background-color: rgba(255, 255, 255, 0.6);

  backdrop-filter: blur(15px);

  border-radius: 16px;

  border: 1px solid rgba(0, 0, 0, 0.08);

  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  .header-title {
    font-size: 1.1rem;

    font-weight: 1000;

    color: #2c3e50;
  }

  .canton-selector-btn {
    color: #34495e !important;

    font-weight: 600;
  }
}

.studio-carousel {
  pointer-events: auto;

  height: 100vh;

  width: 100%;

  overflow-y: auto;

  scroll-snap-type: y mandatory;
}

.studio-slide {
  height: 100vh;

  width: 100%;

  display: flex;

  align-items: flex-end;

  justify-content: center;

  padding: 16px;

  padding-bottom: 80px;

  scroll-snap-align: start;

  position: relative;
}

.studio-card-tiktok {
  border-radius: 24px !important;

  width: 100%;

  max-width: 500px;

  text-align: center;

  display: flex;

  flex-direction: column;

  max-height: calc(100vh - 180px);

  &.theme--light {
    background-color: rgba(255, 255, 255, 0.75) !important;

    backdrop-filter: blur(25px) saturate(180%);

    border: 1px solid rgba(0, 0, 0, 0.08);

    .card-title {
      color: #212121;

      text-shadow: 0 1px 1px rgba(255, 255, 255, 0.5);
    }

    .v-card__subtitle,
    p,
    span,
    .v-icon {
      color: #424242;
    }

    a {
      color: var(--v-primary, #1976d2);

      text-decoration: none;

      font-weight: 600;
    }

    .hours-row {
      border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    }
  }
}

.card-content {
  overflow-y: auto;

  padding: 0 24px;

  text-align: left;
}

.studio-info p {
  display: flex;

  align-items: center;

  margin-bottom: 12px;

  font-size: 1rem;
}

.hours-container {
  max-height: 140px;

  overflow-y: auto;

  padding-right: 8px;
}

.hours-row {
  display: flex;

  justify-content: space-between;

  align-items: flex-start;

  padding: 6px 2px;

  font-size: 0.9rem;

  &:last-child {
    border-bottom: none;
  }
}

.card-actions {
  padding: 16px 24px 24px;

  margin-top: auto;
}

.scroll-indicator {
  bottom: 30px;

  left: 50%;

  position: fixed;

  transform: translateX(-50%);

  pointer-events: none;

  animation: bounce 2.5s infinite ease-in-out;

  opacity: 0.7;

  z-index: 1002;
}

@keyframes bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translate(-50%, 0);
  }

  40% {
    transform: translate(-50%, -15px);
  }

  60% {
    transform: translate(-50%, -7px);
  }
}

:deep(.custom-div-icon) {
  .marker-pin {
    width: 30px;

    height: 30px;

    border-radius: 50% 50% 50% 0;

    background: #5d6d7e;

    position: absolute;

    transform: rotate(-45deg);

    left: 50%;

    top: 50%;

    margin: -15px 0 0 -15px;

    border: 2px solid white;

    transition: background-color 0.3s ease, transform 0.3s ease;

    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);

    &.active {
      background: var(--v-primary, #1976d2);

      transform: rotate(-45deg) scale(1.2);
    }
  }
}

/* Dialog Styles */

.dialog-card {
  display: flex;

  flex-direction: column;

  height: 90vh;

  max-height: 700px;
}

.dialog-header {
  width: 100%;

  padding: 16px 24px;

  border-bottom: 1px solid #e0e0e0;

  background-color: #f9f9f9;

  position: sticky;

  top: 0;

  z-index: 10;
}

.dialog-header-title-bar {
  display: flex;

  justify-content: space-between;

  align-items: center;
}

.studio-dialog-list {
  flex-grow: 1;

  overflow-y: auto;
}

.sticky-subheader {
  position: sticky;

  top: 0;

  background-color: #f0f0f0;

  z-index: 2;

  font-weight: 600 !important;

  color: #333;
}

:deep(.v-text-field.v-text-field--solo:not(.v-text-field--solo-flat) > .v-input__control > .v-input__slot) {
  box-shadow: none !important;

  border: 1px solid #e0e0e0;

  border-radius: 8px;
}

::v-deep .leaflet-control-attribution {
  display: none !important;
}
</style>
